import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Play, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { t, getCurrentLanguage } from '../utils/translations';
import api from './api';
import { HeroLoader } from './Loader';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());
  const [imagesLoaded, setImagesLoaded] = useState({});
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [video, setVideo] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fonction pour extraire le texte brut depuis du HTML
  const stripHTML = (html) => {
    if (!html) return '';
    if (typeof document === 'undefined') {
      return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    }
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
  };

  // Fonction pour tronquer le HTML sans casser les balises
  const truncateHTML = (html, maxLength = 150) => {
    if (!html) return '';
    
    // Vérifier si nous sommes dans un environnement navigateur
    if (typeof document === 'undefined') {
      // Fallback pour le rendu côté serveur
      const textOnly = html.replace(/<[^>]*>/g, '');
      return textOnly.length > maxLength 
        ? textOnly.substring(0, maxLength) + '...' 
        : html;
    }
    
    // Créer un élément temporaire pour parser le HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Obtenir le texte sans HTML
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    if (textContent.length <= maxLength) {
      return html;
    }
    
    // Tronquer le texte
    const truncatedText = textContent.substring(0, maxLength) + '...';
    
    // Créer un nouvel élément avec le texte tronqué
    const truncatedDiv = document.createElement('div');
    truncatedDiv.innerHTML = truncatedText;
    
    return truncatedDiv.innerHTML;
  };

  // Fonction pour traduire dynamiquement le contenu des slides
  const getSlideTranslation = (slide, field) => {
    if (!slide) return '';
    
    const lang = currentLanguage;
    
    // Utiliser les champs traduits directement depuis l'API
    if (field === 'title') {
      if (lang === 'en' && slide.title_en) {
        return slide.title_en;
      }
      return slide.title_fr || slide.title_en || '';
    }
    
    if (field === 'description') {
      if (lang === 'en' && slide.description_en) {
        return slide.description_en;
      }
      return slide.description_fr || slide.description_en || '';
    }
    
    // Fallback pour les anciens champs si nécessaire
    return slide[field] || '';
  };

  // Écouter les changements de langue
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLanguage(getCurrentLanguage());
    };

    // Écouter l'événement de changement de langue
    window.addEventListener('languageChanged', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, []);

  // Récupérer les slides depuis l'API
  useEffect(() => {
    const fetchSlides = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/hero-slides/');
        setSlides(response.data);
        setError(null);
      } catch (err) {
        setError(t('hero.error'));
        setSlides([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSlides();
  }, []);

  // Récupérer la vidéo de présentation
  useEffect(() => {
    const fetchVideo = async () => {
      try {
        const response = await api.get('/api/presentation-videos/');
        if (response.data && response.data.length > 0) {
          setVideo(response.data[0]);
        }
      } catch (err) {
        console.error('Erreur chargement vidéo:', err);
      }
    };
    fetchVideo();
  }, []);

  const openModal = () => {
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = 'unset';
  };

  // Réinitialiser currentSlide quand slides change
  useEffect(() => {
    if (slides.length > 0) {
      setCurrentSlide(0);
    }
  }, [slides.length]);

  // Navigation optimisée avec debounce
  const nextSlide = useCallback(() => {
    if (slides.length === 0 || isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentSlide((prev) => {
      const nextSlide = (prev + 1) % slides.length;
      return nextSlide;
    });
    
    // Réactiver la navigation après la transition
    setTimeout(() => setIsTransitioning(false), 300);
  }, [slides.length, isTransitioning]);

  const prevSlide = useCallback(() => {
    if (slides.length === 0 || isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentSlide((prev) => {
      const prevSlide = (prev - 1 + slides.length) % slides.length;
      return prevSlide;
    });
    
    // Réactiver la navigation après la transition
    setTimeout(() => setIsTransitioning(false), 300);
  }, [slides.length, isTransitioning]);

  // Navigation par indicateur avec debounce
  const goToSlide = useCallback((index) => {
    if (slides.length === 0 || isTransitioning || index === currentSlide) return;
    
    setIsTransitioning(true);
    setCurrentSlide(index);
    
    // Réactiver la navigation après la transition
    setTimeout(() => setIsTransitioning(false), 300);
  }, [slides.length, isTransitioning, currentSlide]);

  // Préchargement des images
  useEffect(() => {
    if (slides.length > 0) {
      slides.forEach((slide, index) => {
        const img = new Image();
        img.onload = () => {
          setImagesLoaded(prev => ({ ...prev, [index]: true }));
        };
        img.onerror = () => {
          setImagesLoaded(prev => ({ ...prev, [index]: false }));
        };
        img.src = slide.image_url || slide.image;
      });
    }
  }, [slides]);

  // Auto-rotation optimisée avec pause pendant interaction
  useEffect(() => {
    if (slides.length <= 1 || isTransitioning) return;
    
    const timer = setInterval(() => {
      nextSlide();
    }, 5000);
    
    return () => clearInterval(timer);
  }, [slides.length, nextSlide, isTransitioning]);

  // Touch/Swipe support pour mobile
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };

  // Loader pendant le chargement
  if (loading) {
    return <HeroLoader />;
  }

  // Affichage d'erreur
  if (error && slides.length === 0) {
    return (
      <div className="w-full py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex justify-center">
            <div className="w-full max-w-4xl">
              <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden">
                <div className="relative h-[280px] md:h-[360px] lg:h-[420px] flex items-center justify-center">
                  <div className="text-center">
                    <div className="h-12 w-12 text-red-500 mx-auto mb-4">
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <p className="text-red-600 text-lg font-medium mb-2">{t('hero.loadingError')}</p>
                    <p className="text-gray-500 text-sm">{error}</p>
                    <button 
                      onClick={() => window.location.reload()} 
                      className="mt-4 bg-[#089556] hover:bg-[#0a7c4a] text-white px-6 py-3 rounded-full text-sm font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
                    >
                      {t('hero.retry')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Pas de slides disponibles
  if (slides.length === 0) {
    return (
      <div className="w-full py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex justify-center">
            <div className="w-full max-w-4xl">
              <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden">
                <div className="relative h-[280px] md:h-[360px] lg:h-[420px] flex items-center justify-center">
                  <div className="text-center">
                    <div className="h-12 w-12 text-gray-400 mx-auto mb-4">
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <p className="text-gray-600 text-lg font-medium mb-2">{t('hero.noSlides')}</p>
                    <p className="text-gray-500 text-sm">{t('hero.slidesComingSoon')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Vérifier que les données du slide actuel sont disponibles
  if (!slides || slides.length === 0) {
    return (
      <div className="w-full py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex justify-center">
            <div className="w-full max-w-4xl">
              <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden">
                <div className="relative h-[280px] md:h-[360px] lg:h-[420px] flex items-center justify-center">
                  <div className="text-center">
                    <div className="h-12 w-12 text-gray-400 mx-auto mb-4">
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <p className="text-gray-600 text-lg font-medium mb-2">{t('hero.noSlides')}</p>
                    <p className="text-gray-500 text-sm">{t('hero.slidesComingSoon')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Vérifier que l'index du slide actuel est valide
  const validCurrentSlide = Math.min(Math.max(0, currentSlide), slides.length - 1);
  
  // Si l'index a changé, mettre à jour currentSlide
  if (validCurrentSlide !== currentSlide) {
    setCurrentSlide(validCurrentSlide);
  }
  
  const currentSlideData = slides[validCurrentSlide];

  // Si les données ne sont pas disponibles, afficher un message d'erreur
  if (!currentSlideData) {
    console.error('Données du slide non disponibles:', { currentSlide, slidesLength: slides.length, slides });
    return (
      <div className="w-full py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex justify-center">
            <div className="w-full max-w-4xl">
              <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden">
                <div className="relative h-[280px] md:h-[360px] lg:h-[420px] flex items-center justify-center">
                  <div className="text-center">
                    <div className="h-12 w-12 text-red-500 mx-auto mb-4">
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <p className="text-red-600 text-lg font-medium mb-2">{t('hero.displayError')}</p>
                    <p className="text-gray-500 text-sm">{t('hero.cannotLoadSlide')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Obtenir la traduction du slide actuel
  const slideTranslation = getSlideTranslation(currentSlideData, 'title');
  const slideDescriptionTranslation = getSlideTranslation(currentSlideData, 'description');

  return (
    <>
      <style>
        {`
          .hero-image-container { transition: opacity 0.3s ease-in-out; }
          .hero-image-loading {
            opacity: 0.7;
            background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
            background-size: 200% 100%;
            animation: shimmer 1.5s infinite;
          }
          @keyframes shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
          .slide-indicator { transition: all 0.3s ease; cursor: pointer; }
          .slide-indicator:hover { transform: scale(1.2); }
          .hero-content { transition: opacity 0.3s ease-in-out; }
          .hero-description-clean p { margin-bottom: 0.75rem; color: #4b5563; line-height: 1.7; }
          .hero-description-clean strong, .hero-description-clean b { color: #1f2937; font-weight: 700; }
          .hero-description-clean ul, .hero-description-clean ol { margin: 0.75rem 0; padding-left: 1.5rem; }
          .hero-description-clean li { margin-bottom: 0.5rem; color: #4b5563; }
          .hero-description-clean a { color: #089556; text-decoration: underline; }
          .video-play-btn { transition: transform 0.3s ease, box-shadow 0.3s ease; }
          .video-play-btn:hover { transform: scale(1.1); box-shadow: 0 0 0 8px rgba(255,255,255,0.3); }
        `}
      </style>

      {/* ── HERO WRAPPER (BAD style) ── */}
      <div className="w-full bg-white pt-6 pb-8">
        <div className="max-w-7xl mx-auto px-4">

          {/* ── TWO-COLUMN LAYOUT ── */}
          <div className="grid grid-cols-1 lg:grid-cols-[65%_35%] gap-6">

            {/* ── LEFT : SLIDER ── */}
            <div className="flex flex-col">
              <div
                className="relative h-[280px] md:h-[360px] lg:h-[400px] hero-image-container overflow-hidden"
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              >
                <div className={`w-full h-full ${!imagesLoaded[currentSlide] ? 'hero-image-loading' : ''}`}>
                  <img
                    key={`slide-${currentSlide}-${currentSlideData.id}`}
                    src={currentSlideData.image_url || currentSlideData.image}
                    alt={slideTranslation || t('hero.slide')}
                    className="w-full h-full object-cover"
                    loading="eager"
                    onLoad={() => setImagesLoaded(prev => ({ ...prev, [currentSlide]: true }))}
                    onError={(e) => { e.target.src = '/slides/slide1.jpg'; setImagesLoaded(prev => ({ ...prev, [currentSlide]: false })); }}
                  />
                </div>

                {/* Prev / Next - BAD style: small subtle chevrons */}
                {slides.length > 1 && (
                  <>
                    <button onClick={prevSlide} disabled={isTransitioning}
                      className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-[#089556] p-2 rounded-full shadow-md z-10 transition-all disabled:opacity-40">
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button onClick={nextSlide} disabled={isTransitioning}
                      className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-[#089556] p-2 rounded-full shadow-md z-10 transition-all disabled:opacity-40">
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </>
                )}

                {/* Dots - bottom right (BAD style) */}
                {slides.length > 1 && (
                  <div className="absolute bottom-3 right-3 flex gap-1.5 z-10">
                    {slides.map((_, i) => (
                      <button key={i} onClick={() => goToSlide(i)} disabled={isTransitioning}
                        className={`slide-indicator rounded-full transition-all ${
                          currentSlide === i ? 'w-2.5 h-2.5 bg-[#089556]' : 'w-2.5 h-2.5 bg-white/70 hover:bg-white'
                        }`}
                        aria-label={`Slide ${i + 1}`}
                      />
                    ))}
                  </div>
                )}

                {/* Spinner */}
                {!imagesLoaded[currentSlide] && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-100/50 z-5">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#089556]" />
                  </div>
                )}
              </div>

              {/* Title link below image (BAD style) */}
              <div className="mt-4">
                {(() => {
                  const basePath = currentLanguage === 'en' ? '/en' : '/fr';
                  const slug = currentSlideData.slug || 'slide';
                  return (
                    <Link
                      to={`${basePath}/full-description/${slug}`}
                      className="text-base md:text-lg text-[#089556] hover:text-[#0a7c4a] hover:underline leading-snug font-normal"
                    >
                      {slideTranslation}
                    </Link>
                  );
                })()}
              </div>
            </div>

            {/* ── RIGHT : VIDEO ── */}
            <div className="flex flex-col">
              <div className="relative h-[280px] md:h-[360px] lg:h-[400px] bg-gray-900 cursor-pointer group overflow-hidden"
                onClick={video ? openModal : undefined}>

                {video?.thumbnail_url ? (
                  <img
                    src={video.thumbnail_url}
                    alt="Vidéo de présentation"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#089556] to-[#07D1EB]" />
                )}

                {/* Light overlay */}
                <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors duration-300" />

                {/* Play button - BAD style: subtle circle with border */}
                {video && (
                  <div className="absolute inset-0 flex items-center justify-center z-10">
                    <div className="video-play-btn w-20 h-20 bg-white/30 backdrop-blur-sm border-2 border-white/80 rounded-full flex items-center justify-center">
                      <Play className="w-8 h-8 text-white ml-1" fill="currentColor" />
                    </div>
                  </div>
                )}

                {!video && (
                  <div className="absolute inset-0 flex items-center justify-center z-10">
                    <p className="text-white/60 text-sm">
                      {currentLanguage === 'fr' ? 'Vidéo bientôt disponible' : 'Video coming soon'}
                    </p>
                  </div>
                )}
              </div>

              {/* Video title link below */}
              {video && (
                <div className="mt-4">
                  <button
                    onClick={openModal}
                    className="text-base md:text-lg text-[#089556] hover:text-[#0a7c4a] hover:underline leading-snug font-normal text-left"
                  >
                    {currentLanguage === 'en' ? (video.title_en || video.title_fr) : (video.title_fr || video.title_en)}
                  </button>
                </div>
              )}
            </div>

          </div>{/* end grid */}

        </div>
      </div>

      {/* ── VIDEO MODAL ── */}
      {isModalOpen && video && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90"
          onClick={closeModal}>
          <div className="relative w-full max-w-5xl" onClick={e => e.stopPropagation()}>
            <button onClick={closeModal}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors">
              <X className="w-8 h-8" />
            </button>
            <div className="relative aspect-video bg-black rounded-lg overflow-hidden shadow-2xl">
              <video src={video.video_url} className="w-full h-full" controls autoPlay />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Hero;